import type {
  ExternalDependencyStatus,
  LifecycleStatus,
} from '~/singlePageApp/screens/LiveStreamScreen/Broadcast/types/LiveStreamEvent';
import type { ParticipantType } from '~/singlePageApp/screens/LiveStreamScreen/Broadcast/types/ParticipantType';
import type { CombinedPollState } from '~/singlePageApp/screens/LiveStreamScreen/Broadcast/types/Polls';
import type { UserType } from '~/singlePageApp/screens/LiveStreamScreen/Broadcast/types/UserType';
import type { NonEmptyArray } from '~/utilities/type-guards';
import type {
  HmsRole,
  Participant720pType,
} from '~/singlePageApp/screens/LiveStreamScreen/Broadcast/types/HmsRole';
import type { PresentableVideoStreamType } from '~/singlePageApp/screens/LiveStreamScreen/Broadcast/types/Presentable';
import type { RegistrationStatus, SlideshowState } from '~/graphql';
import type { Replace } from 'type-fest';

export { default as PLACEHOLDER_IMAGE_URL } from './green_brush_stroke.png';
export { default as DEFAULT_THUMBNAIL } from '~/assets/default-state-thumbnail-1.png';
export { default as DEFAULT_THUMBNAIL_2 } from '~/assets/default-state-thumbnail-2.png';

export const HEADER_HEIGHT = '5'; // units in rem
export const FOOTER_HEIGHT = '5'; // units in rem
export const HEADER_AND_FOOTER_HEIGHT = HEADER_HEIGHT + FOOTER_HEIGHT;
export const MAX_CHAT_MESSAGE_LENGTH = 554;
export const PARTICIPANT_VOLUME = 50;
// Max number of presenters the layouts can display - Burns
export const MAX_PRESENTABLES = 9;
export const MAX_SCREENSHARES = 1;

export const MAX_TITLE_LENGTH = 255;
export const MIN_TITLE_LENGTH = 3;

export const VALID_FEED_TYPES: PresentableVideoStreamType[] = ['camera', 'screenShare'];

const LIVE_LOCAL_STORAGE_NAMESPACE = 'wistia:live:';
export const REGISTRATION_LOCAL_STORAGE_KEY = `${LIVE_LOCAL_STORAGE_NAMESPACE}registeredEvents`;
export const SELECTED_DEVICES_LOCAL_STORAGE_KEY = `${LIVE_LOCAL_STORAGE_NAMESPACE}selectedDevices`;
export const AUDIENCE_MEMBER_ID_LOCAL_STORAGE_KEY = `${LIVE_LOCAL_STORAGE_NAMESPACE}audienceMemberId`;
export const LIVE_DATA_LOCAL_STORAGE_KEY = `${LIVE_LOCAL_STORAGE_NAMESPACE}data`;
export const LIVE_HINTS_LOCAL_STORAGE_KEY = `${LIVE_LOCAL_STORAGE_NAMESPACE}hints`;
export const AUDIO_SETTINGS_LOCAL_STORAGE_KEY = `${LIVE_LOCAL_STORAGE_NAMESPACE}audioSettings`;
export const USER_PREFERENCES_LOCAL_STORAGE_KEY = `${LIVE_LOCAL_STORAGE_NAMESPACE}userPreferences`;
export const WEBCAM_BACKGROUND_LOCAL_STORAGE_KEY = `${LIVE_LOCAL_STORAGE_NAMESPACE}webcamBackground`;
export const LIVEKIT_PREFERENCES_STORAGE_KEY = `${LIVE_LOCAL_STORAGE_NAMESPACE}livekitUserPreferences`;
export const LIFECYCLE_STATUS: {
  [Status in LifecycleStatus as Capitalize<Lowercase<Status>>]: Status;
} = {
  Pending: 'PENDING',
  Started: 'STARTED',
  Starting: 'STARTING',
  Ended: 'ENDED',
  Failed: 'FAILED',
  Ready: 'READY',
  Vodready: 'VODREADY',
  Goinglive: 'GOINGLIVE',
  Live: 'LIVE',
  Disconnected: 'DISCONNECTED',
  Goingoffline: 'GOINGOFFLINE',
  Ending: 'ENDING',
  Recording: 'RECORDING',
};
export const USER_TYPE: {
  [Type in UserType as Capitalize<Lowercase<Type>>]: Type;
} = {
  Host: 'host',
  Panelist: 'panelist',
  Audience: 'audience',
};
export const PARTICIPANT_720P: {
  [Type in Participant720pType as Capitalize<Lowercase<Replace<Type, '-', ''>>>]: Type;
} = {
  Host720p: 'host-720p',
  Panelist720p: 'panelist-720p',
};
export const HMS_ROLE: {
  [Type in HmsRole as Capitalize<Lowercase<Replace<Type, '-', ''>>>]: Type;
} = {
  ...USER_TYPE,
  ...PARTICIPANT_720P,
  Recorder: 'recorder',
};
export const EXTERNAL_DEPENDENCY_STATUS: {
  [Type in ExternalDependencyStatus as Capitalize<Lowercase<Type>>]: Type;
} = {
  Pending: 'pending',
  Failure: 'failure',
  Success: 'success',
  Disabled: 'disabled',
};
export const PARTICIPANT_TYPES: NonEmptyArray<ParticipantType> = [
  USER_TYPE.Host,
  USER_TYPE.Panelist,
];

export const PARTICIPANT_720P_TYPES: NonEmptyArray<Participant720pType> = [
  PARTICIPANT_720P.Host720p,
  PARTICIPANT_720P.Panelist720p,
];

export const HMS_PARTICIPANT_TYPES: NonEmptyArray<Participant720pType | ParticipantType> = [
  ...PARTICIPANT_TYPES,
  ...PARTICIPANT_720P_TYPES,
];

export const USER_TYPES: NonEmptyArray<UserType> = [...PARTICIPANT_TYPES, USER_TYPE.Audience];
export const HMS_ROLES: NonEmptyArray<HmsRole> = [
  ...USER_TYPES,
  ...PARTICIPANT_720P_TYPES,
  'recorder',
];
export const POLL_STATE: {
  [State in CombinedPollState as Capitalize<Lowercase<State>>]: State;
} = {
  Created: 'created',
  Editing: 'editing',
  Started: 'started',
  Starting: 'starting',
  Stopping: 'stopping',
  Stopped: 'stopped',
};

export const REGISTRATION_STATUS: Record<RegistrationStatus, RegistrationStatus> = {
  NEVER_PUBLISHED: 'NEVER_PUBLISHED',
  PUBLISHED: 'PUBLISHED',
  UNPUBLISHED: 'UNPUBLISHED',
};

export const SLIDESHOW_STATE: {
  [State in SlideshowState as Capitalize<Lowercase<State>>]: State;
} = {
  Pending: 'pending',
  Failed: 'failed',
  Ready: 'ready',
  Processing: 'processing',
};

export const ANONYMOUS_USER_FIRST_NAME = 'anonymous';
export const ANONYMOUS_USER_LAST_NAME = 'user';
export const ANONYMOUS_USER_USERNAME = `${ANONYMOUS_USER_FIRST_NAME} ${ANONYMOUS_USER_LAST_NAME}`;
